'use client';

import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { useTransition, isTransitionSkippedRoute } from '@/contexts/TransitionContext';
import { useLenis } from '@/contexts/LenisContext';
import { usePathname, useSearchParams } from 'next/navigation';

import { debug } from '@/lib/debug';
import { useIsLg } from '@/contexts/BreakpointContext';
gsap.registerPlugin(useGSAP);

// Letter data for RIVIVE preloader
interface LetterData {
  svg: string;
  initialYOffset: number;
  marginRight: number;
  mobileMarginRight: number;
}

const RIVIVE_LETTERS: LetterData[] = [
  { svg: 'R_logo.svg', initialYOffset: +6.65, marginRight: 1.7, mobileMarginRight: 1.4 },
  { svg: 'I_1_logo.svg', initialYOffset: +7.56, marginRight: 1.6, mobileMarginRight: 1.3 },
  { svg: 'V_1_logo.svg', initialYOffset: +7.07, marginRight: 1.6, mobileMarginRight: 1.3 },
  { svg: 'I_2_logo.svg', initialYOffset: +8.75, marginRight: 1.6, mobileMarginRight: 1.3 },
  { svg: 'V_2_logo.svg', initialYOffset: +9.625, marginRight: 1.0, mobileMarginRight: 0.83 },
  { svg: 'E_logo.svg', initialYOffset: +5.74, marginRight: 0, mobileMarginRight: 0 },
];

// Animation order for letters
const ANIMATION_ORDER = [
  { letterIndex: 0, delayMultiplier: 0, duration: 1.26 },
  { letterIndex: 2, delayMultiplier: 0.1, duration: 1.96 },
  { letterIndex: 4, delayMultiplier: 0.4, duration: 1.89 },
  { letterIndex: 1, delayMultiplier: 0.5, duration: 1.47 },
  { letterIndex: 5, delayMultiplier: 0.9, duration: 2.03 },
  { letterIndex: 3, delayMultiplier: 1.0, duration: 1.68 },
];

const FADE_OUT_ORDER = [
  { letterIndex: 1, delayMultiplier: 0, duration: 2.0 },
  { letterIndex: 0, delayMultiplier: 0.1, duration: 1.4 },
  { letterIndex: 4, delayMultiplier: 0.5, duration: 2.3 },
  { letterIndex: 3, delayMultiplier: 0.8, duration: 1.5 },
  { letterIndex: 5, delayMultiplier: 0.9, duration: 1.8 },
  { letterIndex: 2, delayMultiplier: 1.0, duration: 1.6 },
];

// Scroll handler for after navigation
const handlePageScroll = (
  targetPathname: string,
  scrollState: any,
  lenis: any,
  isLg: boolean = false,
  searchParams?: URLSearchParams | null,
): void => {
  // debug.log('[handlePageScroll] Called with:', { targetPathname, scrollState, hasLenis: !!lenis, isLg, searchParams: searchParams?.toString() });

  // Check for URL query param-based scrolling (for external/direct links)
  // This takes precedence when scrollState is not set
  if (!scrollState && searchParams) {
    const section = searchParams.get('section');
    if (section === 'referral') {
      const referralSection = document.getElementById('referral-section');
      if (referralSection) {
        const y = referralSection.getBoundingClientRect().top + window.scrollY;
        lenis?.scrollTo(y, { offset: isLg ? -100 : -50, immediate: true, duration: 0 });
        // debug.log('[handlePageScroll] Scrolled to referral section via URL param ?section=referral at y:', y);
        return;
      }
    }
  }

  if (!scrollState) {
    // debug.log('[handlePageScroll] No scroll state, scrolling to top');
    lenis?.scrollTo(0, { immediate: true, duration: 0 });
    return;
  }

  // Determine scroll scenario from state
  let scenario = 'scrollToTop';
  let serviceNavigationLabel: string | undefined;

  if (scrollState.scrollToReferral) {
    scenario = 'scrollToReferral';
  } else if (scrollState.scrollToFAQ) {
    scenario = 'scrollToFAQ';
  } else if (scrollState.scrollToService) {
    scenario = 'scrollToService';
    serviceNavigationLabel = scrollState.serviceNavigationLabel;
  }

  // debug.log('[handlePageScroll] Determined scenario:', scenario, serviceNavigationLabel ? `(service: ${serviceNavigationLabel})` : '');

  // Map scenarios to scroll configs
  const scrollConfigs: Record<string, Record<string, { elementId: string; yOffset: number }>> = {
    '/signup': {
      scrollToReferral: { elementId: 'referral-section', yOffset: isLg ? -100 : -50 },
      scrollToFAQ: { elementId: 'faq', yOffset: isLg ? -100 : -50 },
    },
    '/services': {
      scrollToFAQ: { elementId: 'faq', yOffset: isLg ? -100 : -50 },
    },
    '/': {
      scrollToFAQ: { elementId: 'faq', yOffset: isLg ? -100 : -50 },
    },
  };

  // Handle scroll to service (dynamic element ID)
  if (scenario === 'scrollToService' && serviceNavigationLabel) {
    const element = document.getElementById(serviceNavigationLabel);
    // debug.log('[handlePageScroll] Looking for service element:', serviceNavigationLabel, 'found:', !!element);
    if (element && lenis) {
      const yOffset = isLg ? -100 : -50;
      const rect = element.getBoundingClientRect();
      const y = rect.top + window.scrollY + yOffset;
      // debug.log('[handlePageScroll] Scrolling to service element at y:', y);
      lenis.scrollTo(y, { offset: 0, immediate: true, duration: 0 });
    } else if (!element) {
      // debug.warn('[handlePageScroll] Service element not found:', serviceNavigationLabel);
    } else if (!lenis) {
      // debug.warn('[handlePageScroll] Lenis not available for service scroll');
    }
    return;
  }

  // Get config for current path and scenario
  const pathConfigs = scrollConfigs[targetPathname];
  if (!pathConfigs) {
    // debug.log('[handlePageScroll] No config for path:', targetPathname, '- scrolling to top');
    lenis?.scrollTo(0, { immediate: true, duration: 0 });
    return;
  }

  const config = pathConfigs[scenario];
  if (!config || !config.elementId) {
    // debug.log('[handlePageScroll] No config for scenario:', scenario, 'on path:', targetPathname, '- scrolling to top');
    lenis?.scrollTo(0, { immediate: true, duration: 0 });
    return;
  }

  const element = document.getElementById(config.elementId);
  // debug.log('[handlePageScroll] Looking for element:', config.elementId, 'found:', !!element);
  if (element && lenis) {
    const rect = element.getBoundingClientRect();
    const y = rect.top + window.scrollY + config.yOffset;
    // debug.log('[handlePageScroll] Scrolling to element at y:', y, '(offset:', config.yOffset, ')');
    lenis.scrollTo(y, { offset: 0, immediate: true, duration: 0 });
  } else if (!element) {
    // debug.warn('[handlePageScroll] Element not found:', config.elementId);
    lenis?.scrollTo(0, { immediate: true, duration: 0 });
  } else if (!lenis) {
    // debug.warn('[handlePageScroll] Lenis not available for scroll');
  }
};

interface TransitionOverlayProps {
  children: React.ReactNode;
}

export default function TransitionOverlay({ children }: TransitionOverlayProps) {
  const {
    transitionType,
    phase,
    markContentReady,
    completeTransition,
    executeNavigation,
    pendingNavigation,
    scrollState,
    clearScrollState,
  } = useTransition();

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isLg = useIsLg();
  const { lenis } = useLenis();

  // Refs for preloader
  const preloaderRef = useRef<HTMLDivElement>(null);
  const letterRefs = useRef<(HTMLImageElement | null)[]>([]);

  // Refs for page transition / navigation overlay
  const overlayRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);

  // Refs for exit animation cleanup (timeline + RAF handle)
  const exitTimelineRef = useRef<gsap.core.Timeline | null>(null);
  const exitRafRef = useRef<number | null>(null);

  // Local state
  const [lettersAnimatedIn, setLettersAnimatedIn] = useState(false);
  const [pageTransitionAnimatedIn, setPageTransitionAnimatedIn] = useState(false);

  // Track which overlays should be visible
  // Start showOverlay true to match static overlay, prevent flash during first render
  const [showPreloader, setShowPreloader] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);

  // Track if this is the very first client-side render (before any state updates from context)
  const [isInitialMount, setIsInitialMount] = useState(true);

  // On mount, immediately determine what to show
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // debug.log('[TransitionOverlay] Initial mount check');

    // Check if this is from an animated navigation or page refresh
    const scrollStateStr = sessionStorage.getItem('navigationScrollState');
    const visited = sessionStorage.getItem('hasVisited');

    if (scrollStateStr || visited !== null) {
      // debug.log('[TransitionOverlay] Page refresh or navigation - keeping React overlay (already true)');
      // showOverlay is already true, keep it
    } else {
      // debug.log('[TransitionOverlay] First visit - switching to preloader');
      // First visit = preloader, hide overlay and show preloader
      setShowOverlay(false);
      setShowPreloader(true);
    }

    setIsInitialMount(false);
  }, []);

  // Determine what to show based on transition type and phase
  useEffect(() => {
    // debug.log('[TransitionOverlay] STATE CHANGE', {
      // transitionType,
      // phase,
      // currentShowPreloader: showPreloader,
      // currentShowOverlay: showOverlay,
      // isInitialMount,
    // });

    // Don't update during initial mount - let the mount effect handle it
    if (isInitialMount) {
      // debug.log('[TransitionOverlay] Skipping STATE CHANGE during initial mount');
      return;
    }

    if (transitionType === 'preloader') {
      // debug.log('[TransitionOverlay] Setting showPreloader=true, showOverlay=false');
      setShowPreloader(true);
      setShowOverlay(false);
    } else if (transitionType === 'page-transition') {
      // debug.log('[TransitionOverlay] Setting showPreloader=false, showOverlay=true (page-transition)');
      setShowPreloader(false);
      setShowOverlay(true);
      // Reset animation flag when starting a new page-transition
      if (phase === 'showing') {
        // debug.log('[TransitionOverlay] Resetting pageTransitionAnimatedIn flag');
        setPageTransitionAnimatedIn(false);
      }
    } else if (transitionType === 'navigation') {
      setShowPreloader(false);
      // Show overlay during exiting, navigating, entering, and ready-to-hide phases
      // Overlay will be hidden by the animation when fade-out completes
      if (phase === 'exiting' || phase === 'navigating' || phase === 'entering' || phase === 'ready-to-hide') {
        // debug.log('[TransitionOverlay] Setting showPreloader=false, showOverlay=true (navigation phase:', phase, ')');
        setShowOverlay(true);
      } else {
        // debug.log('[TransitionOverlay] Setting showPreloader=false, showOverlay=false (navigation phase:', phase, ')');
        setShowOverlay(false);
      }
    } else {
      // debug.log('[TransitionOverlay] Setting showPreloader=false, showOverlay=false (none)');
      // transitionType === 'none'
      setShowPreloader(false);
      setShowOverlay(false);
    }
  }, [transitionType, phase, isInitialMount]);

  // Block scrolling during transitions
  useEffect(() => {
    if (phase === 'exiting' || phase === 'navigating') {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else if (phase === 'complete') {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
  }, [phase]);

  // Listen for popstate to IMMEDIATELY show overlay (prevent content flash)
  // This runs synchronously before React state updates, so the overlay appears instantly
  useEffect(() => {
    const handlePopStateOverlay = () => {
      const destinationPath = window.location.pathname;

      // Skip for routes that have transitions disabled entirely (beauty-center, /booking, /login).
      // Forcing the overlay visible on those routes leaves it stuck: no transition phases ever
      // fire to fade it back out, so the user sees a cream-coloured blank screen until refresh.
      if (isTransitionSkippedRoute(destinationPath)) return;

      // debug.log('[TransitionOverlay] Popstate: immediately showing overlay to prevent flash');

      // Immediately show overlay via GSAP (before React state updates)
      const overlay = overlayRef.current;
      const inner = innerRef.current;
      const logo = logoRef.current;

      if (overlay) {
        gsap.set(overlay, {
          opacity: 1,
          visibility: 'visible',
        });
      }

      // Set inner and logo to animation initial states (consistent with page-transition animation)
      if (inner) {
        gsap.set(inner, {
          borderWidth: '0px',
          borderRadius: '0px',
          filter: 'blur(30px)',
        });
      }

      if (logo) {
        gsap.set(logo, {
          opacity: 0,
          filter: 'blur(30px)',
        });
      }
    };

    window.addEventListener('popstate', handlePopStateOverlay);
    return () => window.removeEventListener('popstate', handlePopStateOverlay);
  }, []);

  // ============================================
  // PRELOADER ANIMATIONS
  // ============================================

  // Preloader letter animation (fade in)
  useGSAP(() => {
    if (transitionType !== 'preloader' || phase !== 'showing') return;
    if (!showPreloader) return;

    const letters = letterRefs.current.filter(Boolean);
    if (!letters.length) return;

    // debug.log('[TransitionOverlay] Starting preloader animation');

    // Set initial states
    letters.forEach((letter, index) => {
      if (letter) {
        gsap.set(letter, {
          opacity: 0,
          filter: 'blur(30px)',
          y: `${RIVIVE_LETTERS[index].initialYOffset}rem`,
        });
      }
    });

    // Animate letters in
    const tl = gsap.timeline({
      onComplete: () => setLettersAnimatedIn(true),
    });

    ANIMATION_ORDER.forEach(({ letterIndex, delayMultiplier, duration }) => {
      const letter = letters[letterIndex];
      if (letter) {
        tl.to(letter, {
          opacity: 1,
          filter: 'blur(0px)',
          y: 0,
          duration,
          ease: 'power2.out',
        }, delayMultiplier);
      }
    });
  }, [transitionType, phase, showPreloader]);

  // Preloader fade out (when content is ready)
  useEffect(() => {
    if (transitionType !== 'preloader' || phase !== 'ready-to-hide' || !lettersAnimatedIn) return;

    const letters = letterRefs.current.filter(Boolean);
    const preloader = preloaderRef.current;
    if (!letters.length || !preloader) return;

    // debug.log('[TransitionOverlay] Starting preloader fade out');
    window.dispatchEvent(new CustomEvent('heroVideoPrepare'));

    const tl = gsap.timeline();

    // Fade out letters
    FADE_OUT_ORDER.forEach(({ letterIndex, duration }) => {
      const letter = letters[letterIndex];
      if (letter) {
        tl.to(letter, {
          opacity: 0,
          filter: 'blur(30px)',
          y: '-10rem',
          duration,
          ease: 'power2.in',
        }, 0);
      }
    });

    // Fade out background
    tl.to(preloader, {
      opacity: 0,
      duration: 0.5,
      onStart: () => {
        // Scroll to referral section if needed (synced with preloader fade out)
        handlePageScroll(pathname, null, lenis, isLg, searchParams);
      },
      onComplete: () => {
        setShowPreloader(false);
        completeTransition();
      },
    }, 1.0);
  }, [transitionType, phase, lettersAnimatedIn, completeTransition, pathname, lenis, isLg, searchParams]);

  // ============================================
  // PAGE TRANSITION ANIMATIONS (initial page load, not first visit)
  // ============================================

  useGSAP(() => {
    // debug.log('[TransitionOverlay] page-transition useGSAP triggered', {
      // transitionType,
      // phase,
      // showOverlay,
      // isLg,
    // });

    if (transitionType !== 'page-transition' || phase !== 'showing') {
      // debug.log('[TransitionOverlay] Skipping page-transition animation - wrong type or phase');
      return;
    }
    if (!showOverlay) {
      // debug.log('[TransitionOverlay] Skipping page-transition animation - overlay not visible');
      return;
    }

    const overlay = overlayRef.current;
    const inner = innerRef.current;
    const logo = logoRef.current;

    // debug.log('[TransitionOverlay] page-transition refs check', {
      // hasOverlay: !!overlay,
      // hasInner: !!inner,
      // hasLogo: !!logo,
    // });

    if (!overlay || !inner || !logo) {
      // debug.warn('[TransitionOverlay] Missing refs for page-transition animation');
      return;
    }

    // debug.log('[TransitionOverlay] Starting page-transition showing animation', {
      // borderWidth: isLg ? '24px' : '12px',
      // borderRadius: isLg ? '56px' : '32px',
    // });

    // Set initial states - overlay starts visible with content animating in
    gsap.set(overlay, { opacity: 1 });
    gsap.set(inner, {
      borderWidth: '0px',
      borderRadius: '0px',
      filter: 'blur(30px)',
    });
    gsap.set(logo, {
      opacity: 0,
      filter: 'blur(30px)',
    });

    // debug.log('[TransitionOverlay] Initial GSAP states set for page-transition');

    // Animate in
    const tl = gsap.timeline({
      // onStart: () => debug.log('[TransitionOverlay] page-transition timeline started'),
      onComplete: () => {
        // debug.log('[TransitionOverlay] page-transition timeline completed');
        setPageTransitionAnimatedIn(true);
      },
    });

    tl.to(inner, {
      borderWidth: isLg ? '24px' : '12px',
      borderRadius: isLg ? '56px' : '32px',
      filter: 'blur(0px)',
      duration: 1.0,
      ease: 'power2.out',
      // onStart: () => debug.log('[TransitionOverlay] Border animation started'),
      // onComplete: () => debug.log('[TransitionOverlay] Border animation completed'),
    }, 0)
    .to(logo, {
      opacity: 1,
      filter: 'blur(0px)',
      duration: 1.0,
      ease: 'power2.out',
      // onStart: () => debug.log('[TransitionOverlay] Logo fade-in started'),
      // onComplete: () => debug.log('[TransitionOverlay] Logo fade-in completed'),
    }, 0.2);
  }, [transitionType, phase, isLg, showOverlay]);

  // Page transition fade out
  useEffect(() => {
    // debug.log('[TransitionOverlay] page-transition fade-out effect triggered', {
      // transitionType,
      // phase,
      // pageTransitionAnimatedIn,
    // });

    if (transitionType !== 'page-transition' || phase !== 'ready-to-hide') {
      // debug.log('[TransitionOverlay] Skipping page-transition fade-out - wrong type or phase');
      return;
    }

    if (!pageTransitionAnimatedIn) {
      // debug.log('[TransitionOverlay] Skipping page-transition fade-out - show animation not complete yet');
      return;
    }

    const overlay = overlayRef.current;
    const inner = innerRef.current;
    const logo = logoRef.current;

    // debug.log('[TransitionOverlay] page-transition fade-out refs check', {
      // hasOverlay: !!overlay,
      // hasInner: !!inner,
      // hasLogo: !!logo,
    // });

    if (!overlay || !inner || !logo) {
      // debug.warn('[TransitionOverlay] Missing refs for page-transition fade-out');
      return;
    }

    // debug.log('[TransitionOverlay] Starting page-transition fade out animation');
    window.dispatchEvent(new CustomEvent('heroVideoPrepare'));

    const tl = gsap.timeline({
      // onStart: () => debug.log('[TransitionOverlay] page-transition fade-out timeline started'),
      // onComplete: () => debug.log('[TransitionOverlay] page-transition fade-out timeline completed'),
    });

    tl.to(logo, {
      opacity: 0,
      filter: 'blur(30px)',
      duration: 1.0,
      ease: 'power2.in',
      // onStart: () => debug.log('[TransitionOverlay] Logo fade-out started'),
      // onComplete: () => debug.log('[TransitionOverlay] Logo fade-out completed'),
    }, 0)
    .to(inner, {
      borderWidth: '0px',
      borderRadius: '0px',
      filter: 'blur(30px)',
      duration: 1.0,
      ease: 'power2.in',
      // onStart: () => debug.log('[TransitionOverlay] Border collapse started'),
      onComplete: () => {
        // debug.log('[TransitionOverlay] Border collapse completed');
        // Scroll to referral section if needed (synced with transition complete)
        handlePageScroll(pathname, null, lenis, isLg, searchParams);
      },
    }, 0)
    .to(overlay, {
      opacity: 0,
      duration: 0.5,
      ease: 'power2.in',
      // onStart: () => debug.log('[TransitionOverlay] Overlay fade-out started'),
      onComplete: () => {
        // debug.log('[TransitionOverlay] Overlay fade-out completed, calling completeTransition');
        // Ensure visibility is hidden via GSAP (in case React hasn't re-rendered yet)
        gsap.set(overlay, { visibility: 'hidden' });
        setShowOverlay(false);
        completeTransition();
      },
    }, 0.8);
  }, [transitionType, phase, pageTransitionAnimatedIn, completeTransition, pathname, lenis, isLg, searchParams]);

  // ============================================
  // NAVIGATION ANIMATIONS (in-app navigation)
  // ============================================

  // Navigation exit animation (user clicked a link)
  useGSAP(() => {
    if (transitionType !== 'navigation' || phase !== 'exiting') return;

    // debug.log('[TransitionOverlay] Starting navigation exit animation');

    // Kill any existing exit animation to prevent race conditions
    if (exitRafRef.current !== null) {
      cancelAnimationFrame(exitRafRef.current);
      exitRafRef.current = null;
    }
    if (exitTimelineRef.current) {
      exitTimelineRef.current.kill();
      exitTimelineRef.current = null;
    }

    // Ensure overlay is visible
    setShowOverlay(true);

    // Wait for next frame to ensure DOM is updated after setShowOverlay(true)
    exitRafRef.current = requestAnimationFrame(() => {
      exitRafRef.current = null;

      const overlay = overlayRef.current;
      const inner = innerRef.current;
      const logo = logoRef.current;
      if (!overlay || !inner || !logo) {
        // debug.warn('[TransitionOverlay] Missing refs for exit animation');
        return;
      }

      // Set initial states - overlay starts invisible
      gsap.set(overlay, { opacity: 0 });
      gsap.set(inner, {
        borderWidth: '0px',
        borderRadius: '0px',
        filter: 'blur(30px)',
      });
      gsap.set(logo, {
        opacity: 0,
        filter: 'blur(30px)',
      });

      // Animate overlay in (exit animation)
      const tl = gsap.timeline({
        onComplete: () => {
          // debug.log('[TransitionOverlay] Exit animation complete, executing navigation');
          exitTimelineRef.current = null;
          executeNavigation();
        },
      });

      exitTimelineRef.current = tl;

      tl.to(overlay, {
        opacity: 1,
        duration: 0.5,
        ease: 'power2.inOut',
      }, 0)
      .to(inner, {
        borderWidth: isLg ? '24px' : '12px',
        borderRadius: isLg ? '56px' : '32px',
        filter: 'blur(0px)',
        duration: 1.0,
        ease: 'power2.out',
      }, 0.2)
      .to(logo, {
        opacity: 1,
        filter: 'blur(0px)',
        duration: 1.0,
        ease: 'power2.out',
      }, 0.2);
    });

    // Cleanup: cancel RAF and kill timeline if effect re-runs or component unmounts
    return () => {
      if (exitRafRef.current !== null) {
        cancelAnimationFrame(exitRafRef.current);
        exitRafRef.current = null;
      }
      if (exitTimelineRef.current) {
        exitTimelineRef.current.kill();
        exitTimelineRef.current = null;
      }
    };
  }, [transitionType, phase, isLg, executeNavigation]);

  // Navigation enter animation (new page loaded after navigation)
  useEffect(() => {
    if (transitionType !== 'navigation' || phase !== 'ready-to-hide') return;

    const overlay = overlayRef.current;
    const inner = innerRef.current;
    const logo = logoRef.current;
    if (!overlay || !inner || !logo) return;

    // debug.log('[TransitionOverlay] Starting navigation enter fade out');
    window.dispatchEvent(new CustomEvent('heroVideoPrepare'));

    // Ensure overlay is in the "shown" state before fading out
    gsap.set(overlay, { opacity: 1 });
    gsap.set(inner, {
      borderWidth: isLg ? '24px' : '12px',
      borderRadius: isLg ? '56px' : '32px',
      filter: 'blur(0px)',
    });
    gsap.set(logo, {
      opacity: 1,
      filter: 'blur(0px)',
    });

    const tl = gsap.timeline();

    tl.to(logo, {
      opacity: 0,
      filter: 'blur(30px)',
      duration: 1.0,
      ease: 'power2.in',
    }, 0)
    .to(inner, {
      borderWidth: '0px',
      borderRadius: '0px',
      filter: 'blur(30px)',
      duration: 1.0,
      ease: 'power2.in',
      onComplete: () => {
        // Handle scrolling after navigation - use current pathname
        // debug.log('[TransitionOverlay] Navigation fade-out inner complete, calling handlePageScroll', {
          // pathname,
          // scrollState,
          // hasLenis: !!lenis,
          // searchParams: searchParams?.toString(),
        // });
        handlePageScroll(pathname, scrollState, lenis, isLg, searchParams);
        // NOTE: clearScrollState is called in overlay.onComplete AFTER completeTransition
        // to prevent the effect from re-running with scrollState=null
      },
    }, 0)
    .to(overlay, {
      opacity: 0,
      duration: 0.5,
      ease: 'power2.in',
      onComplete: () => {
        // Ensure visibility is hidden via GSAP (in case React hasn't re-rendered yet)
        gsap.set(overlay, { visibility: 'hidden' });
        setShowOverlay(false);
        completeTransition();
        // Clear scroll state AFTER completeTransition - this ensures phase='complete'
        // before scrollState changes, preventing effect re-run with null scrollState
        clearScrollState();
      },
    }, 0.8);

    // Cleanup: kill the timeline if effect re-runs (prevents duplicate animations)
    return () => {
      tl.kill();
    };
  }, [transitionType, phase, isLg, completeTransition, pathname, scrollState, lenis, clearScrollState, searchParams]);

  // ============================================
  // RENDER
  // ============================================

  // Determine initial overlay opacity based on transition type
  // - page-transition: Start visible (opacity: 1) to cover page immediately on load
  // - navigation: Start invisible (opacity: 0) and fade in during exit animation
  // If transitionType is still 'none' but showOverlay is true (first render), assume page-transition
  let initialOverlayOpacity = 0;
  if (transitionType === 'page-transition') {
    initialOverlayOpacity = 1;
  } else if (transitionType === 'none' && showOverlay) {
    // First render before context updates - assume page-transition
    initialOverlayOpacity = 1;
  }

  // debug.log('[TransitionOverlay] RENDER', {
    // transitionType,
    // phase,
    // showPreloader,
    // showOverlay,
    // initialOverlayOpacity,
    // isLg,
  // });

  return (
    <>
      {/* Preloader overlay (first visit) */}
      {showPreloader && (
        <div
          ref={preloaderRef}
          data-transition-overlay
          aria-hidden="true"
          style={{ zIndex: 10000 }}
          className="fixed inset-0 bg-bg-dark-primary flex flex-col items-center justify-center"
        >
          <div className="flex items-center justify-center">
            {RIVIVE_LETTERS.map((letterData, index) => (
              <img
                key={index}
                ref={(el) => { letterRefs.current[index] = el; }}
                src={`/${letterData.svg}`}
                alt=""
                style={{
                  marginRight: isLg ? `${letterData.marginRight}rem` : `${letterData.mobileMarginRight}rem`,
                  opacity: 0, // Start invisible, GSAP will animate
                }}
                className="h-[1.875rem] lg:h-[2.25rem] w-auto object-contain"
              />
            ))}
          </div>
        </div>
      )}

      {/* Page transition / Navigation overlay - ALWAYS MOUNTED to prevent content flash on popstate */}
      <div
        ref={overlayRef}
        data-transition-overlay
        aria-hidden="true"
        className="fixed inset-0 flex items-center justify-center"
        style={{
          backgroundColor: '#D8C7B4',
          zIndex: 10000,
          // When hidden: opacity 0, visibility hidden (but still in DOM for instant popstate response)
          // When shown: opacity controlled by GSAP or initialOverlayOpacity
          opacity: showOverlay ? initialOverlayOpacity : 0,
          visibility: showOverlay ? 'visible' : 'hidden',
          pointerEvents: 'none',
        }}
      >
        <div
          ref={innerRef}
          className="w-full h-full flex items-center justify-center bg-bg-light-primary"
          style={{ borderStyle: 'solid', borderColor: '#D8C7B4' }}
        >
          <img
            ref={logoRef}
            src="/Page_Transition_Logomark_Registration.svg"
            alt=""
            style={{ opacity: 0 }} // Start invisible, GSAP will animate
            className="h-[4.9375rem] lg:h-[6.4375rem] w-auto object-contain"
          />
        </div>
      </div>

      {/* Page content */}
      {children}
    </>
  );
}
